'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Truck, Building2, ShieldCheck, Copy, ArrowRight, CheckCircle2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useRouter } from 'next/navigation';

type Role = 'driver' | 'employer' | 'admin';
type Mode = 'login' | 'signup';

interface AuthFormData {
  name?: string;
  email: string;
  password: string;
  role?: Role;
  remember?: boolean;
}

const DEMO_CREDENTIALS = [
  { role: 'admin' as Role, email: 'admin@hiretruckers365.com', password: 'HT365#Admin2026', label: 'Admin', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  { role: 'driver' as Role, email: 'marcus.t@drivers.ht365.com', password: 'CDL#Driver2026', label: 'Driver', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  { role: 'employer' as Role, email: 'hiring@kowalskifreight.com', password: 'Carrier#2026', label: 'Employer', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
];

const ROLE_CONFIG = {
  driver: { icon: Truck, label: 'CDL Driver', desc: 'Looking for work', color: 'text-amber-400', border: 'border-amber-500', bg: 'bg-amber-500/10' },
  employer: { icon: Building2, label: 'Carrier / Employer', desc: 'Hiring drivers', color: 'text-orange-400', border: 'border-orange-500', bg: 'bg-orange-500/10' },
  admin: { icon: ShieldCheck, label: 'Platform Admin', desc: 'Admin access', color: 'text-purple-400', border: 'border-purple-500', bg: 'bg-purple-500/10' },
};

export default function AuthScreen() {
  const router = useRouter();
  const [mode, setMode] = useState<Mode>('login');
  const [selectedRole, setSelectedRole] = useState<Role>('driver');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<AuthFormData>();

  const onSubmit = async (data: AuthFormData) => {
    setIsLoading(true);
    // TODO: Backend — POST /api/auth/login or /api/auth/register with { email, password, role }
    await new Promise((r) => setTimeout(r, 1200));
    setIsLoading(false);

    const matched = DEMO_CREDENTIALS.find(
      (c) => c.email === data.email && c.password === data.password
    );

    if (mode === 'login' && !matched) {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
      return;
    }

    const role = matched?.role ?? selectedRole;
    toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!', {
      description: `Signed in as ${role}`,
    });

    if (role === 'admin') router.push('/admin-dashboard');
    else if (role === 'driver') router.push('/driver-profile-onboarding-screen');
    else if (role === 'employer') router.push('/employer-job-post-screen');
    else router.push('/user-dashboard');
  };

  const autofillCredential = (cred: typeof DEMO_CREDENTIALS[0]) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    setSelectedRole(cred.role);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex-col items-center justify-center p-16">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(245,158,11,0.08)_0%,_transparent_70%)]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(rgba(245,158,11,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.5) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />

        <div className="relative z-10 max-w-md text-center">
          <div className="flex items-center justify-center gap-3 mb-10">
            <AppLogo size={48} />
            <span className="font-bold text-2xl text-zinc-100">
              HireTruckers<span className="text-amber-400">365</span>
            </span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight mb-4 leading-tight">
            Where CDL Drivers and Carriers{' '}
            <span className="gradient-text">Connect Smarter.</span>
          </h2>
          <p className="text-zinc-400 text-sm leading-relaxed mb-10">
            Private, admin-controlled introductions. No browsing, no spam — just the right match at the right time.
          </p>

          {/* Features */}
          <div className="space-y-3 text-left">
            {[
              'Drivers never pay — 100% free, always',
              'Employers pay 20% only on successful hire',
              'Admin-reviewed matches before every introduction',
              'All 50 states covered — local, regional, OTR',
            ].map((item) => (
              <div key={`feature-${item.slice(0, 20)}`} className="flex items-start gap-3">
                <CheckCircle2 size={15} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-zinc-400">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-zinc-100">
              HireTruckers<span className="text-amber-400">365</span>
            </span>
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-xl bg-zinc-900 border border-zinc-800 p-1 mb-8">
            {(['login', 'signup'] as Mode[]).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  mode === m
                    ? 'bg-amber-500 text-zinc-950' :'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Role selector */}
          <div className="mb-6">
            <label className="label">I am a...</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.entries(ROLE_CONFIG) as [Role, typeof ROLE_CONFIG.driver][]).map(([role, cfg]) => (
                <button
                  key={`role-${role}`}
                  type="button"
                  onClick={() => setSelectedRole(role)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all duration-150 ${
                    selectedRole === role
                      ? `${cfg.bg} ${cfg.border} ${cfg.color}`
                      : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:border-zinc-600'
                  }`}
                >
                  <cfg.icon size={18} />
                  <span className="text-xs font-medium leading-tight text-center">{cfg.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <label className="label">Full Name</label>
                <input
                  {...register('name', { required: 'Full name is required' })}
                  type="text"
                  placeholder="Marcus Thompson"
                  className="input-field"
                />
                {errors.name && <p className="error-text">{errors.name.message}</p>}
              </div>
            )}

            <div>
              <label className="label">Email Address</label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
                type="email"
                placeholder="you@company.com"
                className="input-field"
              />
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>

            <div>
              <label className="label">Password</label>
              <div className="relative">
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••"
                  className="input-field pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="error-text">{errors.password.message}</p>}
            </div>

            {mode === 'login' && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    {...register('remember')}
                    type="checkbox"
                    className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 accent-amber-500"
                  />
                  <span className="text-xs text-zinc-400">Remember me</span>
                </label>
                <button type="button" className="text-xs text-amber-400 hover:text-amber-300 transition-colors">
                  Forgot password?
                </button>
              </div>
            )}

            {mode === 'signup' && (
              <p className="text-xs text-zinc-500">
                By creating an account, you agree to our{' '}
                <a href="#" className="text-amber-400 hover:underline">Terms of Service</a> and{' '}
                <a href="#" className="text-amber-400 hover:underline">Privacy Policy</a>.
              </p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3 text-sm justify-center"
              style={{ minWidth: '100%' }}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                  <ArrowRight size={16} />
                </span>
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 p-4 rounded-xl bg-zinc-900 border border-zinc-800">
            <p className="text-xs font-semibold text-zinc-500 mb-3 uppercase tracking-wider">Demo Accounts — Click to Autofill</p>
            <div className="space-y-2">
              {DEMO_CREDENTIALS.map((cred) => (
                <div
                  key={`cred-${cred.role}`}
                  className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-zinc-800/60 border border-zinc-700/50 hover:border-zinc-600 transition-all cursor-pointer group"
                  onClick={() => autofillCredential(cred)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`badge ${cred.bg} ${cred.color} border flex-shrink-0`}>{cred.label}</span>
                    <span className="text-xs text-zinc-400 truncate font-mono">{cred.email}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(cred.password); }}
                      className="p-1 text-zinc-600 hover:text-zinc-300 transition-colors"
                      aria-label="Copy password"
                    >
                      <Copy size={12} />
                    </button>
                    <span className={`text-xs font-medium ${cred.color} group-hover:underline`}>Use</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-xs text-zinc-600 mt-6">
            <Link href="/landing-page" className="text-zinc-500 hover:text-zinc-300 transition-colors">
              ← Back to homepage
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}